import { useState } from 'react';
import { useUpdateContribution } from '@/hooks/leverageQueries';
import { useToast } from '@/hooks/useToast';
import { Field, Modal, MoneyInput } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import type { LeverageContribution } from '@/lib/types';

interface ContributionModalProps {
  /** O aporte a corrigir (só o administrador). O aporte novo nasce do comprovante de depósito — ver NewReceiptModal. */
  contribution: LeverageContribution;
  onClose: () => void;
}

/**
 * Correção de um aporte lançado à mão, de antes do comprovante virar o aporte.
 * O que veio de comprovante se corrige pelo comprovante.
 */
export function ContributionModal({ contribution, onClose }: ContributionModalProps) {
  const toast = useToast();
  const update = useUpdateContribution();

  const [amount, setAmount] = useState(contribution.amount);
  const [date, setDate] = useState(contribution.date);
  const [time, setTime] = useState(contribution.time);
  const [note, setNote] = useState(contribution.note ?? '');
  const [error, setError] = useState<string | null>(null);

  function problem(): string | null {
    if (amount <= 0) return 'Informe o valor aportado.';
    if (!date) return 'Informe a data do aporte.';
    return null;
  }

  async function submit() {
    const found = problem();
    if (found) return setError(found);
    setError(null);
    try {
      await update.mutateAsync({ id: contribution.id, amount, date, time, note: note.trim() || null });
      toast.success('Aporte corrigido.');
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title="Corrigir aporte"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={update.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={update.isPending}>
            {update.isPending ? <span className="spinner" /> : <IconCheck />} Salvar
          </button>
        </>
      }
    >
      <div className="field">
        <span className="field-label">Cliente</span>
        <div className="lead-picked"><span className="truncate" style={{ fontWeight: 500 }}>{contribution.lead.name}</span></div>
      </div>

      <div className="grid grid3">
        <Field label="Valor aportado (R$) *">
          <MoneyInput value={amount} onChange={setAmount} autoFocus />
        </Field>
        <Field label="Data *">
          <input className="inp" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Hora">
          <input className="inp" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </Field>
      </div>

      <Field label="Observação">
        <input className="inp" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Opcional" maxLength={500} />
      </Field>

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
